import React from "react";
import { useRef } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Checkbox, Container, Dimmer, Divider, Dropdown, Form, Grid, Header, Input, Loader, Progress, Segment, Table } from "semantic-ui-react";
import usePageData from "../state-manipulation/hooks/usePageData";
import { setSelectedPage } from "../state-manipulation/reducers/page-data/types";
import AxiosWrapper from "../utils/AxiosWrapper";
import * as JsSearch from "js-search";
import PropTypes from "prop-types";
import moment from "moment";
import PageHeader from "../components/common/PageHeader";
import { DateInput, Drop } from "semantic-ui-calendar-react";
import useUserData from "../state-manipulation/hooks/useUserData";
import { PostReciprocalInvoices } from "../api/XeroAPI";


const inventoryData = {
  "PLT": {
    "items": [
      {
        "name": "Condensed Milk",
        "costAmount": "30.45",
        "glAccount": {
          "name": "COGS: Coffee",
          "code": "50403",
          "LANGTHANGCOFFEE": {
            "name": "COGS: Coffee Drinks",
            "code": "5703",
          }
        }
      },
      {
        "name": "Saigon Phin - 1700g Case",
        "costAmount": "279.30",
        "glAccount": {
          "name": "COGS: Coffee",
          "code": "50403",
          "LANGTHANGCOFFEE": {
            "name": "COGS: Coffee Drinks",
            "code": "5703"
          }
        }
      },
      {
        "name": "Saigon Phin - 1700g bag",
        "costAmount": "23.28",
        "glAccount": {
          "name": "COGS: Coffee",
          "code": "50403",
          "LANGTHANGCOFFEE": {
            "name": "COGS: Coffee Drinks",
            "code": "5703"
          }
        }
      }
    ]
  }
}

export default function SelfCheckout({ selectedPage, pageName, className }) {
  const { company } = useParams();
  const [, pageDispatch] = usePageData();
  const [userData, userDispatch] = useUserData();
  const [isLoading, setIsLoading] = useState(false);
  const [statusPercentage, setStatusPercentage] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(moment().format('MM/DD/YY'));
  const [selectedVendor, setSelectedVendor] = useState("");
  const [quantities, setQuantities] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDraft, setIsDraft] = useState(true);
  const [autoPay, setAutoPay] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);

const toggleAdvanced = () => setShowAdvanced(!showAdvanced);



  const handleInoiceDateChange = (e, { value }) => {
    setInvoiceDate(value);
  };

  const handleVendorChange = (e, { value }) => {
    setSelectedVendor(value);
    setQuantities({});
  };

  const items = React.useMemo(() => {
    const vendorData = inventoryData[selectedVendor];
    if (!vendorData) return [];
    const companyKey = company.toUpperCase().replaceAll(" ", "");
    return vendorData.items.filter(
      (item) => item.glAccount && item.glAccount[companyKey]
    );
  }, [selectedVendor, company]);


  const handleQtyChange = (index, value) => {
    const newQuantities = { ...quantities, [index]: parseInt(value || 0) };
    setQuantities(newQuantities);
  };

  const getLineTotal = (cost, qty) => parseFloat(cost) * (qty || 0);

  const grandTotal = items.reduce((acc, item, i) =>
    acc + getLineTotal(item.costAmount, quantities[i]), 0
  );

  const vendorOptions = Object.entries(inventoryData)
    .filter(([vendor, data]) => {
      return data.items.some(item => item.glAccount && item.glAccount[company.toUpperCase().replaceAll(" ","")]);
    })
    .map(([vendor]) => ({
      key: vendor,
      text: vendor,
      value: vendor
    }));


  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setIsSubmitError(false);
      setStatusMessage("Creating Invoices...")
      const lineItems = items
        .map((item, i) => {
          const qty = quantities[i] || 0;
          if (qty <= 0) return null;
          return {
            name: item.name,
            unitAmount: parseFloat(item.costAmount),
            glAccount: item.glAccount,
            quantity: qty,
            lineTotal: parseFloat(item.costAmount) * qty,
          };
        })
        .filter(Boolean);

      const invoiceData = {
        vendor: selectedVendor,
        date: invoiceDate,
        total: grandTotal,
        lineItems,
        isDraft: isDraft,
        autoPay: autoPay
      };
      setStatusPercentage(50);
      let res = await PostReciprocalInvoices(company, { invoice: invoiceData });
      if (res?.status !== 200) {
        throw new Error(res?.data?.message || "Failed to create invoices");
      }

      setStatusPercentage(100);
      setStatusMessage('Invoices created')
      setIsLoading(false);
      console.log("🚀 ~ handleSubmit ~ res:", res)
    } catch (error) {
      setIsLoading(false);
      setStatusPercentage(100);
      setStatusMessage(error.message || "Unknown error");
      console.error(error);
    }
  };


  useEffect(() => {
    pageDispatch({
      type: setSelectedPage,
      payload: {
        selectedPage: selectedPage,
      },
    });


  }, []);

  useEffect(() => {
    if (vendorOptions.length > 0) {
      setSelectedVendor(vendorOptions[0].value);
    } else {
      setSelectedVendor('');
    }
  }, [company, vendorOptions]);


  return (
    <div className={'content-container ' + className}>
      <PageHeader pageName={pageName} orgName={userData.selectedOrg?.name} />
      <Segment>
        <Dimmer active={isLoading}>
          <Loader />
        </Dimmer>
        <Progress percent={statusPercentage} autoSuccess error={isSubmitError}>
          {statusMessage}
        </Progress>
        <Form>
          <Form.Group widths="equal">
            <DateInput
              closable
              label="Date*"
              placeholder="start"
              dateFormat="MM/DD/YY"
              className="date-picker"
              value={invoiceDate}
              onChange={handleInoiceDateChange}
              hideMobileKeyboard={true}
            />
            <Form.Select
              fluid
              label="Vendor"
              options={vendorOptions}
              value={selectedVendor}
              onChange={handleVendorChange}
            />
          </Form.Group>

          <Divider />
          <Header as="h4">Items from {selectedVendor}</Header>
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Item</Table.HeaderCell>
                <Table.HeaderCell>Unit Cost</Table.HeaderCell>
                <Table.HeaderCell>Qty</Table.HeaderCell>
                <Table.HeaderCell>Total</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item, i) => (
                <Table.Row key={i}>
                  <Table.Cell>{item.name}</Table.Cell>
                  <Table.Cell>${item.costAmount}</Table.Cell>
                  <Table.Cell>
                    <Input
                      type="number"
                      min="0"
                      value={quantities[i] || ''}
                      onChange={(e) => handleQtyChange(i, e.target.value)}
                      placeholder="0"
                      size="mini"
                    />
                  </Table.Cell>
                  <Table.Cell>${getLineTotal(item.costAmount, quantities[i]).toFixed(2)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan="3" textAlign="right"><strong>Total</strong></Table.HeaderCell>
                <Table.HeaderCell><strong>${grandTotal.toFixed(2)}</strong></Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
          <Divider />
          <div style={{ marginBottom: '1rem' }}>
            <Checkbox
              label="Show Advanced Options"
              toggle
              checked={showAdvanced}
              onChange={toggleAdvanced}
            />
          </div>

          {showAdvanced && (
            <Segment>
              <Form.Group grouped>
                <Form.Field>
                  <Checkbox
                    label="Draft"
                    checked={isDraft}
                    onChange={(e, data) => setIsDraft(data.checked)}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    label="Auto Pay Bills"
                    checked={autoPay}
                    onChange={(e, data) => setAutoPay(data.checked)}
                  />
                </Form.Field>
              </Form.Group>
            </Segment>
          )}

          <Form.Button primary onClick={handleSubmit}>
            Submit Invoice
          </Form.Button>
        </Form>
      </Segment>
    </div>
  );
}

SelfCheckout.propTypes = {
  selectedPage: PropTypes.string,
  pageName: PropTypes.string,
  className: PropTypes.string
};

SelfCheckout.defaultProps = {
  selectedPage: "SELF CHECKOUT",
  pageName: "Self Checkout",
  className: ''
};
